import React, { useState } from "react";
import { Table, Tag, Tooltip, Empty, Alert, Button } from "antd";
import { FONT } from "../../styles/typography";

export const CATEGORY_LABEL = {
  facility: "設施", room: "客房", dining: "餐飲", service: "服務",
  amenity: "備品", activity: "活動", other: "其他",
};

// 卡片已經很長，預設只給前 10 條；排行尾端通常是一兩則的零星需求
const PREVIEW = 10;

const headerStyle = {
  fontSize: FONT.body, fontWeight: 600, color: "#595959",
  marginBottom: 6, paddingBottom: 3, borderBottom: "1px solid #f0f0f0",
};
const nowrap = { whiteSpace: "nowrap" };

const trendMark = (count, prev) => (count > prev ? "↑" : count < prev ? "↓" : "→");

// 可排序欄位取值。去年同期為 null 代表「沒有對比資料」不是 0，不論方向都排最後
const SORT_VALUE = {
  count: (row) => row.count,
  prev: (row) => row.prev_count,
  total: (row) => row.total_count,
};

/** 依欄位排序全部需求 (穩定排序，同值維持原順序)；沒有排序時維持後端給的順序。 */
export function sortNeeds(needs, sort) {
  const get = sort && sort.order && SORT_VALUE[sort.key];
  if (!get) return needs;
  const dir = sort.order === "ascend" ? 1 : -1;
  return needs
    .map((row, i) => ({ row, i, v: get(row) }))
    .sort((a, b) => {
      if (a.v == null || b.v == null) return (a.v == null) - (b.v == null) || a.i - b.i;
      return (a.v - b.v) * dir || a.i - b.i;
    })
    .map((x) => x.row);
}

/**
 * 住客需求排行。資料來自逐則標記的 SQL 彙總，與 AI 期別摘要是兩條獨立的線 ——
 * 摘要沒產生或失敗時這裡照常顯示。
 *
 * 單期樣本小，只看本期則數分不出「新冒出來」和「長年都有人提」，所以同一列並排
 * 去年同期與累計。
 */
export default function GuestNeedsSection({ data, error, onOpen, prevLabel = "去年同期" }) {
  const [expanded, setExpanded] = useState(false);
  const [sort, setSort] = useState(null); // { key, order }

  if (error) {
    return (
      <div style={{ marginTop: 16 }}>
        <div style={headerStyle}>住客需求</div>
        <Alert type="error" showIcon message="讀取住客需求失敗" description="請稍後重試；若持續發生請通知系統管理者。" />
      </div>
    );
  }
  // 讀取中不放 Spin：區塊在卡片最下方，資料回來前佔位只會讓版面跳動
  if (!data || data.enabled === false) return null;

  const needs = data.needs || [];
  // 排序自己做、不交給 antd：預設只顯示前 10 條，antd 只會排畫面上這 10 條，
  // 點「累計」時排第 11 名以後、累計最高的需求永遠不會浮上來
  const sorted = sortNeeds(needs, sort);
  const rows = expanded ? sorted : sorted.slice(0, PREVIEW);
  const sortable = (key) => ({
    sorter: true,
    sortDirections: ["descend", "ascend"],
    sortOrder: sort && sort.key === key ? sort.order : null,
  });
  // item_ids 有上限：count 比它大時，抽屜自己的「（N 則）」只是最近的幾則，標題要講清楚，
  // 否則會被誤認成該需求的總則數
  const open = (row) => {
    const ids = row.item_ids || [];
    const capped = row.count > ids.length;
    onOpen(row.item_ids, `「${row.name}」的住客需求${capped ? ` (最近 ${ids.length} 則)` : ""}`);
  };

  const columns = [
    {
      title: "需求", dataIndex: "name", key: "name", width: 240, ellipsis: { showTitle: false },
      render: (name, row) => (
        <span style={nowrap}>
          <Tag style={{ marginInlineEnd: 6 }}>{CATEGORY_LABEL[row.category] || row.category}</Tag>
          <Tooltip placement="topLeft" title={name}><span>{name}</span></Tooltip>
        </span>
      ),
    },
    {
      title: "本期", key: "count", width: 190, ...sortable("count"),
      render: (_, row) => <span style={nowrap}>{`${row.count} (明確 ${row.explicit}・推得 ${row.derived})`}</span>,
    },
    {
      title: prevLabel, key: "prev", width: 120, ...sortable("prev"),
      render: (_, row) => (
        <span style={nowrap}>{row.prev_count == null ? "-" : `${row.prev_count} ${trendMark(row.count, row.prev_count)}`}</span>
      ),
    },
    {
      title: "累計", key: "total", width: 160, ...sortable("total"),
      render: (_, row) => <span style={nowrap}>{`${row.total_count}・自 ${(row.first_seen || "").slice(0, 7)}`}</span>,
    },
    {
      title: "住客原話", dataIndex: "latest_quote", key: "quote", width: 320, ellipsis: { showTitle: false },
      render: (quote) => (quote ? <Tooltip placement="topLeft" title={quote}>{quote}</Tooltip> : <span style={{ color: "#bfbfbf" }}>-</span>),
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={headerStyle}>
        住客需求 {needs.length}
        {data.pending_count > 0 && (
          <span style={{ fontSize: FONT.caption, fontWeight: 400, color: "#8c8c8c", marginLeft: 8 }}>
            另有 {data.pending_count} 則尚未分析
          </span>
        )}
      </div>
      {!needs.length ? (
        <Empty description="本期沒有辨識出住客需求" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1030 }}
            showSorterTooltip={false}
            onChange={(_, __, sorter) => setSort(sorter && sorter.order ? { key: sorter.columnKey, order: sorter.order } : null)}
            onRow={(row) => ({
              // 整列可點：與「依據回饋」同一個抽屜。tabIndex 讓鍵盤使用者也選得到
              onClick: () => open(row),
              onKeyDown: (e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                open(row);
              },
              tabIndex: 0,
              style: { cursor: "pointer" },
            })}
          />
          {needs.length > PREVIEW && !expanded && (
            <Button type="link" size="small" onClick={() => setExpanded(true)} style={{ paddingLeft: 0, marginTop: 4 }}>
              查看全部 {needs.length} 項
            </Button>
          )}
        </>
      )}
    </div>
  );
}
